// cliDownload.mjs — CLI 下载页的纯逻辑（第一百零九批 D）。无 React、无 DOM。
//
// ── 这一页要回答的是一个**新人真的会问**的问题 ───────────────────────────────
// 「这是什么、我为什么需要它、下载完第一步做什么、我的 key 从哪来」。
// 其中第四问是死锁的那一半：他**没有** key，所以第一步不可能是 `selfupdate`
//（那条命令要 key 才跑得起来）。页面的步骤顺序因此是钉死的：
//   1. 解压  → 2. 去「密件」取 key  → 3. 写进 dc-cli.jsonc 再 describe 自检
// `cli.selfupdate` 那句只作为**装好之后**的补充出现，绝不能排进这三步里——
// 排进去就是让每一个新人在第一步撞上一堵墙，然后来问人。判据在 cliDownload.test.mjs。
//
// ── 整包的校验和为什么要显示出来 ────────────────────────────────────────────
// 浏览器下载不留任何可核对的东西，而"我下的这份完整吗"是接入方第一个会问的问题。
// manifest 的 `bundle` 块带 {path, bytes, sha256}，页面把它印出来（端点也把它放进
// 响应头，`curl -I` 一下就有）。少了这块 ⇒ 老镜像（本批之前构建的），端点回 503
// CLI_DIST_ABSENT ——那是**部署侧的缺口**，不是用户点错了，文案必须这么说。

// 三步的顺序是这一页的承重结构（头注）。步骤文案在 i18n，这里只钉顺序与身份。
export const CLI_STEP_KEYS = ['cli.steps.unpack', 'cli.steps.key', 'cli.steps.config'];

// 下载直链。**同源 + cookie 会话**，走普通 <a href>（仓内先例：工单附件与
// AdminReposPage 的下载）——fetch 一个 zip 再造 blob 会把整包读进内存，且下载
// 进度、断点续传、浏览器的"另存为"全都要自己重造一遍。
// ⚠ 端点判据（endpoint-callers）要的是**整段路径字面量**，所以真正的 href 直接写在
// CliDownloadPage.jsx 里，而不是从这里拼——拼出来的路径那条判据看不见。
export const CLI_BUNDLE_PATH = '/api/cli/bundle';

// ⚠ `Number(null)` 是 0，所以不能只靠 Number.isFinite（一个缺席的 bytes 会变成
// "0 KB"，看起来像一个真的很小的包）。
const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// manifest 响应 → 受控形状。
// `hasBundle` 是**要分岔的那一位**：老镜像的 manifest 有 version/files、没有 bundle，
// 此时下载按钮必须不可点并说出原因（点下去会得到 503）。fail-closed：形状不全
// 一律当作没有整包——一个下载不到东西的按钮比没有按钮更糟。
export function normalizeCliManifest(data) {
  const version = typeof data?.version === 'string' && data.version.trim()
    ? data.version.trim() : '';
  // 第一百八十三批：清单若自带 minSupportedVersion 就一并带出（管理页「需升级」徽记
  // 的第二来源；第一来源是每条应答上的 X-DC-CLI-Min-Version 头）。老清单没有这一
  // 位 ⇒ 空串，页面回落到响应头——两处都没有时不出红徽记，绝不猜一个地板。
  const minVersion = typeof data?.minSupportedVersion === 'string' && data.minSupportedVersion.trim()
    ? data.minSupportedVersion.trim() : '';
  const files = Array.isArray(data?.files) ? data.files : [];
  const b = data?.bundle;
  const sha = typeof b?.sha256 === 'string' ? b.sha256 : '';
  const bytes = num(b?.bytes);
  // sha256 逐字符验形（64 位小写十六进制），与后端 sha256_hex_shape 同一条：
  // 一串不是十六进制的"校验和"本来也核对不了任何东西，印出来只会给人假的安心。
  const shaOk = /^[0-9a-f]{64}$/.test(sha);
  const hasBundle = Boolean(b && typeof b === 'object') && shaOk && bytes != null && bytes > 0;
  return {
    version,
    minVersion,
    fileCount: files.length,
    bundleBytes: hasBundle ? bytes : null,
    bundleSha: hasBundle ? sha : '',
    hasBundle,
  };
}

// 字节 → KB（整数，向上取整到 1）。给 MB/GB 分档没有意义：这个包是几十 KB 的
// 量级，一个恒为 "0.0 MB" 的显示等于没显示。
export function kbOf(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.max(1, Math.round(n / 1024));
}
